import moment from 'moment-timezone'
import { combineReducers } from 'redux'
import request, { generateNotyMessage } from 'utils/request'
import { downloadBlob, getBaseSiteIdFromStudy, pluralize, stripHtmlTags } from 'utils/misc'
import { logSentryEvent } from 'utils/sentry'
import { today } from 'utils/time'
import { browserHistory } from 'utils/navigation'
import { actions as notyActions } from 'layouts/ErrorBox'
import { loadingActions } from 'store/loader'
import { modalActions } from 'store/modal'
import { sendingActions } from 'store/sender'
import { toUTCMidnightSeconds } from 'utils/time'
import {
  DEFAULT_LANG,
  DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION,
  INSTRUMENT_TYPE_MAP,
  MODAL_CLASSES_MAP,
  GROUP_TYPE_MAP,
} from 'utils/constants'
import { formatLangCode } from 'utils/locales'
import { convertAdvancedOptions } from 'utils/hoc/AdvancedOptionsHOC'
import STRINGS from 'utils/strings'
import { fetchCohorts } from '../../../../Participants/routes/ParticipantsPage/modules/Participants'
import { initializeCheckedCohorts } from '../../../../Participants/routes/CreateParticipant/modules/CreateParticipant'
import { onFetchInstrument, deployInstrument } from '../../../../Instruments/routes/Instrument/modules/Instrument'
import { actions as instrumentsActions } from '../../../../Instruments/routes/InstrumentsPage/modules/Instruments'
import { getStudyVersionID, isVersionUatOrLive } from 'utils/studyConfigUtils'

// ACTION CONSTANTS

const UPDATE_ALERT_FIELD = 'UPDATE_ALERT_FIELD'
const UPDATE_ALERT_SCHEDULE = 'UPDATE_ALERT_SCHEDULE'
const RESET_ALERT = 'RESET_ALERT'
const SET_ALERT = 'SET_ALERT'
const SET_ALERT_ERRORS = 'SET_ALERT_ERRORS'
const CLEAR_ANNOUNCEMENT_ERRORS = 'CLEAR_ANNOUNCEMENT_ERRORS'
const TOGGLE_COHORT = 'TOGGLE_COHORT'
const TOGGLE_ANNOUNCEMENT_TRANSLATIONS = 'TOGGLE_ANNOUNCEMENT_TRANSLATIONS'
const ADD_ANNOUNCEMENT_TRANSLATION = 'ADD_ANNOUNCEMENT_TRANSLATION'
const DELETE_ANNOUNCEMENT_TRANSLATION = 'DELETE_ANNOUNCEMENT_TRANSLATION'
const UPDATE_ANNOUNCEMENT_TRANSLATION = 'UPDATE_ANNOUNCEMENT_TRANSLATION'
const SET_ANNOUNCEMENT_TRANSLATIONS = 'SET_ANNOUNCEMENT_TRANSLATIONS'

// ACTION CREATORS

const resetAnnouncement = () => {
  return {
    type: RESET_ALERT,
  }
}

const setAnnouncement = payload => {
  return {
    type: SET_ALERT,
    payload,
  }
}

const updateAnnouncementField = (field, value, errorKey) => {
  return {
    type: UPDATE_ALERT_FIELD,
    field,
    value,
    errorKey,
  }
}

const updateAnnouncementSchedule = (field, value, errorKey) => {
  return {
    type: UPDATE_ALERT_SCHEDULE,
    field,
    value,
    errorKey,
  }
}

const toggleAnnouncementTranslations = localeId => {
  return {
    type: TOGGLE_ANNOUNCEMENT_TRANSLATIONS,
    localeId,
  }
}

const addAnnouncementTranslation = localeId => {
  return {
    type: ADD_ANNOUNCEMENT_TRANSLATION,
    localeId,
  }
}

const updateAnnouncementTranslation = ({ key, localeId, value }) => {
  return {
    type: UPDATE_ANNOUNCEMENT_TRANSLATION,
    key,
    localeId,
    value,
  }
}

const deleteAnnouncementTranslation = localeId => {
  return {
    type: DELETE_ANNOUNCEMENT_TRANSLATION,
    localeId,
  }
}

const setAnnouncementTranslations = translations => {
  return {
    type: SET_ANNOUNCEMENT_TRANSLATIONS,
    translations,
  }
}

const clearAnnouncementErrors = () => ({ type: CLEAR_ANNOUNCEMENT_ERRORS })

//
// ASYNC ACTIONS
//

const COMMUNICATION_REQUEST_MAP = {
  announcement: (studyID, communicationId) => _getAnnouncement(studyID, communicationId),
  announcementInstrument: (studyID, communicationId) => onFetchInstrument(studyID, communicationId),
  sms: (studyID, communicationId) => _getSMS(studyID, communicationId),
}

const initializeAnnouncementPage = ({ studyID, communicationId, isAnnouncement = true, isInstrument }) => {
  return dispatch => {
    dispatch(fetchCohorts(studyID))
    if (communicationId) {
      /**
       * if the announcement is an instrument (in `instrument_architecture_version` 2 and above),
       * we want to fetch an instrument
       */
      if (isInstrument) {
        // V2: hydrate from instruments API only (avoid announcement.json 500s / logout).
        dispatch(onFetchInstrument(studyID, communicationId)).then(instrument => {
          if (!instrument) {
            dispatch(loadingActions.stopLoader(true))
            return
          }
          const payload = buildAnnouncementFromInstrument(instrument, communicationId)
          dispatch(setAnnouncement(payload))
          if (payload.schedule?.cohort?.type === 'study_groups') {
            dispatch(initializeCheckedCohorts(payload.schedule.cohort.filter.include))
          }
          dispatch(loadingActions.stopLoader(true))
        })
      } else {
        const requestFunc = isAnnouncement ? COMMUNICATION_REQUEST_MAP.announcement : COMMUNICATION_REQUEST_MAP.sms
        dispatch(requestFunc(studyID, communicationId)).then(payload => {
          dispatch(setAnnouncement(payload))
          if (payload.schedule.cohort.type === 'study_groups') {
            dispatch(initializeCheckedCohorts(payload.schedule.cohort.filter.include))
          }
        })
      }
    } else {
      dispatch(resetAnnouncement())
      dispatch(initializeCheckedCohorts([]))
    }
  }
}

const saveAnnouncement = (redirect, isAnnouncement = true, advancedOptions) => {
  return async (dispatch, getState) => {
    // TODO: SP
    const studyVersionID = getStudyVersionID(getState()?.location)
    try {
      const savedCommunicationResponse = await dispatch(
        validateAndSaveCommunication(false, isAnnouncement, advancedOptions),
      )
      if (!savedCommunicationResponse || typeof savedCommunicationResponse !== 'object') return
      const { studyID, deploy } = savedCommunicationResponse
      if (!deploy && studyID) {
        dispatch(sendingActions.stopSender())
        setTimeout(() => {
          browserHistory.push(`/studies/${studyID}/${studyVersionID}/communication`)
          dispatch(sendingActions.resetSender())
        }, 500)
      }
    } catch (err) {
      if (__DEV__) console.error(err.message)
    }
  }
}

const saveAndDeploy = (isAnnouncement = true, advancedOptions) => {
  return dispatch => {
    return dispatch(
      modalActions.openModal({
        content: `Are you sure you want to deploy this ${isAnnouncement ? 'announcement' : 'SMS message'
          }? This cannot be undone and ${isAnnouncement ? 'announcements' : 'SMS messages'} cannot be deleted.`,
        className: MODAL_CLASSES_MAP.confirmation,
        onConfirm: () => dispatch(_saveAndDeploy(isAnnouncement, advancedOptions)),
      }),
    )
  }
}

const _saveAndDeploy = (isAnnouncement = true, advancedOptions) => {
  return (dispatch, getState) => {
    // TODO: SP
    const studyVersionID = getStudyVersionID(getState()?.location)
    return dispatch(validateAndSaveCommunication(true, isAnnouncement, advancedOptions))
      .then(response => {
        if (!response || typeof response !== 'object') return
        const { studyID, id, isInstrument = false } = response
        if (isInstrument) {
          const successCallback = () => {
            dispatch(sendingActions.stopSender(true))
            browserHistory.push(`/studies/${studyID}/${studyVersionID}/communication`)
            setTimeout(() => {
              dispatch(sendingActions.resetSender(true))
            }, 500)
          }
          return dispatch(deployInstrument({ studyID, instrumentID: id, isAnnouncement: true, successCallback }))
        }
        return dispatch(_deployCommunication(studyID, id, isAnnouncement))
      })
      .catch(err => {
        if (__DEV__) console.error(err?.message || err)
      })
  }
}

const validateAndSaveCommunication = (deploy = false, isAnnouncement = true, advancedOptions) => {
  return (dispatch, getState) => {
    const { study, announcementReducer, participantReducer, location } = getState()
    const convertedAdvancedOptions = convertAdvancedOptions(advancedOptions)
    const { announcement } = announcementReducer
    const { currentStudy, studyVersionList } = study
    const studyID = currentStudy.id
    const siteID = getBaseSiteIdFromStudy(study)

    const announcementCopy = JSON.parse(JSON.stringify(announcement))
    announcementCopy.content = announcement.content
    const announcementCopyScheduleCohort = announcementCopy.schedule.cohort

    /**
     * If an announcement has translations
     */
    if (announcement.translations) {
      const translationKeys = Object.keys(announcement.translations)
      const primaryTranslationId = announcement.translations[DEFAULT_LANG] ? DEFAULT_LANG : translationKeys[0]
      announcementCopy.content = announcementCopy.translations[primaryTranslationId].content
      announcementCopy.title = announcementCopy.translations[primaryTranslationId].title
    }

    delete announcementCopy.disabledLangs

    switch (announcementCopyScheduleCohort.type) {
      case GROUP_TYPE_MAP.cohorts:
        announcementCopy.schedule.cohort.filter.include = Object.keys(participantReducer.checkedCohorts).map(numStr =>
          parseInt(numStr, 10),
        )
        break
      case GROUP_TYPE_MAP.sites:
        const studyVersionID = getStudyVersionID(location)
        const hasStudyPromotion = !!currentStudy?.config?.study_promotion
        if (hasStudyPromotion) {
          const studyVersion = studyVersionList?.find(item => item?.version_id === studyVersionID)
          const currentVersionStatus = studyVersion?.version_status || ''
          // Initialize env_config if it doesn't exist
          if (!announcementCopy.schedule.env_config) {
            announcementCopy.schedule.env_config = {}
          }
          announcementCopy.schedule.env_config[currentVersionStatus] = announcementCopy.schedule.cohort.filter.site_ids
        }
        break
      default:
        break
    }

    if (convertedAdvancedOptions) announcementCopy.schedule.cohort = [...convertedAdvancedOptions]
    const errors = _getAnnouncementErrors(announcementCopy)
    dispatch({ type: SET_ALERT_ERRORS, errors })
    if (Object.keys(errors).length === 0) {
      return dispatch(_saveCommunicationToDB(studyID, siteID, announcementCopy, deploy, isAnnouncement))
    }
    if (errors.translations) {
      const numTranslationsError = Object.keys(errors.translations).length
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(
            pluralize(
              numTranslationsError,
              'There is 1 incomplete translation',
              `There are ${numTranslationsError} incomplete translations`,
              false,
            ),
            false,
          ),
        }),
      )
    } else {
      dispatch(
        notyActions.showError({
          text: generateNotyMessage('Please correct the errors marked in red before proceeding.', false),
        }),
      )
    }
    return Promise.reject(new Error(isAnnouncement ? 'Invalid announcement.' : 'Invalid SMS message'))
  }
}

/**
 * Map CSV / instrument locale codes (e.g. `en`, `en_us`, `es`) to admin locale ids (`en_US`, `es_ES`).
 */
export const resolveAnnouncementLocaleId = (code, knownLocaleIds = []) => {
  if (!code) return code
  if (code === 'en') return DEFAULT_LANG

  const normalized = formatLangCode(code)
  if (knownLocaleIds.includes(code)) return code
  if (knownLocaleIds.includes(normalized)) return normalized

  // Short codes like `es` → first matching `es_*` locale when available
  if (!String(normalized).includes('_')) {
    const match = knownLocaleIds.find(id => id === normalized || id.startsWith(`${normalized}_`))
    if (match) return match
  }

  return normalized
}

export const normalizeTranslationKeys = (translations, knownLocaleIds = []) => {
  if (!translations || !Object.keys(translations).length) return translations

  const result = {}
  Object.keys(translations).forEach(code => {
    const localeId = resolveAnnouncementLocaleId(code, knownLocaleIds)
    result[localeId] = {
      ...(result[localeId] || {}),
      ...(translations[code] || {}),
    }
  })
  return result
}

/**
 * Build admin-panel translation shape `{ [localeId]: { title, content } }` from announcement/instrument JSON.
 * Order:
 * 1. CSV fields (`title_translations` / `content_translations`) from announcement.json
 * 2. `metadata.announcement_content.translations`
 * 3. `metadata.display_name_translations` + `metadata.other_languages` (instruments API after CSV import)
 * 4. legacy root `translations`
 */
export const extractAnnouncementTranslations = (payload, knownLocaleIds = []) => {
  if (!payload) return null

  const titleByLang = payload?.title_translations?.['title-cti']?.title || {}
  const contentByLang = payload?.content_translations?.['content-co']?.content || {}
  const csvLangCodes = [...new Set([...Object.keys(titleByLang), ...Object.keys(contentByLang)])]

  if (csvLangCodes.length) {
    const result = {}
    // Process non-`en` first so a full `en_US` column is not overwritten by short `en`
    const orderedCodes = [...csvLangCodes.filter(code => code !== 'en'), ...csvLangCodes.filter(code => code === 'en')]

    orderedCodes.forEach(code => {
      const localeId = resolveAnnouncementLocaleId(code, knownLocaleIds)
      if (code === 'en' && result[localeId]) return

      result[localeId] = {
        title: titleByLang[code] ?? result[localeId]?.title ?? '',
        content: contentByLang[code] ?? result[localeId]?.content ?? '',
      }
    })

    return result
  }

  const announcementTranslations = payload?.metadata?.announcement_content?.translations
  if (announcementTranslations && Object.keys(announcementTranslations).length) {
    return normalizeTranslationKeys(announcementTranslations, knownLocaleIds)
  }

  const displayNameTranslations = payload?.metadata?.display_name_translations || {}
  const otherLanguages = payload?.metadata?.other_languages || []
  const displayLangCodes = [...new Set([...Object.keys(displayNameTranslations), ...otherLanguages])]

  if (displayLangCodes.length) {
    const result = {}
    const rootTitle =
      payload?.metadata?.announcement_content?.title || payload?.title || payload?.metadata?.display_name || ''
    const rootContent = payload?.metadata?.announcement_content?.content || payload?.content || ''

    displayLangCodes.forEach(code => {
      const localeId = resolveAnnouncementLocaleId(code, knownLocaleIds)
      const titleFromDisplay =
        displayNameTranslations[code] ??
        displayNameTranslations[localeId] ??
        displayNameTranslations[String(code).toLowerCase()]

      result[localeId] = {
        title: titleFromDisplay ?? (localeId === DEFAULT_LANG ? rootTitle : ''),
        content: localeId === DEFAULT_LANG ? rootContent : result[localeId]?.content ?? '',
      }
    })

    return result
  }

  const rootTranslations = payload?.translations
  return rootTranslations && Object.keys(rootTranslations).length
    ? normalizeTranslationKeys(rootTranslations, knownLocaleIds)
    : null
}

const hasTranslationEntries = translations =>
  translations && Object.keys(translations).length > 0

const getKnownLocaleIds = (getState, enforcedLanguage) => {
  const { study } = getState() || {}
  return [
    DEFAULT_LANG,
    ...(enforcedLanguage?.languages?.map(lang => lang.id) || []),
    ...(study?.locales?.map(locale => locale.id) || []),
  ]
}

const buildAnnouncementFromInstrument = (instrument, communicationId) => {
  const announcementContent = instrument?.metadata?.announcement_content || {}
  const payload = {
    ...instrument,
    id: instrument?.id || communicationId,
    title: announcementContent.title || instrument?.title || instrument?.metadata?.display_name || '',
    content: announcementContent.content || '',
    schedule: instrument?.metadata?.schedule,
    // translations are derived in _formatAnnouncement via extractAnnouncementTranslations
    num_received: instrument?.num_received,
    num_sent: instrument?.num_sent,
  }
  if (!hasTranslationEntries(payload.translations)) {
    delete payload.translations
  }
  return payload
}

const mergeAnnouncementInstrumentAndJson = (instrument, announcementJson, instrumentId) => {
  const base = buildAnnouncementFromInstrument(instrument, instrumentId)
  if (!announcementJson) return base

  return {
    ...base,
    id: base.id || announcementJson.id || instrumentId,
    title: announcementJson.title || base.title,
    content: announcementJson.content || base.content,
    schedule: announcementJson.schedule || base.schedule,
    title_translations: announcementJson.title_translations || base.title_translations,
    content_translations: announcementJson.content_translations || base.content_translations,
    num_received: base.num_received ?? announcementJson.num_received,
    num_sent: base.num_sent ?? announcementJson.num_sent,
    metadata: {
      ...(base.metadata || {}),
      ...(announcementJson.metadata || {}),
      schedule: announcementJson.metadata?.schedule || base.metadata?.schedule || base.schedule,
      display_name_translations:
        base.metadata?.display_name_translations ||
        announcementJson.metadata?.display_name_translations,
      other_languages: base.metadata?.other_languages || announcementJson.metadata?.other_languages,
      announcement_content: {
        ...(base.metadata?.announcement_content || {}),
        ...(announcementJson.metadata?.announcement_content || {}),
      },
    },
  }
}

const _formatAnnouncement = _announcement => {
  const resultAnnouncement = { ..._announcement }
  resultAnnouncement.content = _announcement.content

  const announcementContent = _announcement?.metadata?.announcement_content
  if (announcementContent) {
    if (!resultAnnouncement.title && announcementContent.title) {
      resultAnnouncement.title = announcementContent.title
    }
    if (!resultAnnouncement.content && announcementContent.content) {
      resultAnnouncement.content = announcementContent.content
    }
  }

  // Prefer CSV instrument fields over stale root/metadata.translations after import
  const translations = normalizeTranslationKeys(extractAnnouncementTranslations(_announcement))
  const { deployed } = _announcement

  if (hasTranslationEntries(translations)) {
    resultAnnouncement.translations = translations
    resultAnnouncement.disabledLangs = []
    Object.keys(translations).forEach(localeId => {
      resultAnnouncement.translations[localeId] = {
        title: translations[localeId]?.title ?? '',
        content: translations[localeId]?.content ?? '',
      }
      /**
       * If the announcement has been deployed and has translations, we need to know
       * which languages to not allow edits in for message and content. Translations
       * that have already been deployed and exist in the announcement should not be editable.
       */
      if (deployed) resultAnnouncement.disabledLangs.push(localeId)
    })
  } else {
    delete resultAnnouncement.translations
  }
  return resultAnnouncement
}

const parseNewTranslations = ({ payloadTranslations, enforcedLanguage }) => {
  const resultTranslations = { ...payloadTranslations }

  // If uploaded languages are not supported by a study, they will be discarded
  if (enforcedLanguage) {
    const payloadLangIdArr = Object.keys(payloadTranslations)
    const enforcedLangIdArr = enforcedLanguage.languages.map(enforcedLangObj => {
      return enforcedLangObj.id
    })
    enforcedLangIdArr.push(DEFAULT_LANG)

    const diffArr = payloadLangIdArr.filter(langId => !enforcedLangIdArr.includes(langId))
    diffArr.forEach(diffLangId => {
      delete resultTranslations[diffLangId]
    })
  }

  Object.keys(resultTranslations).forEach(langId => {
    resultTranslations[langId].content = payloadTranslations[langId].content
  })
  return resultTranslations
}

//
// API CALLS
//

const _getAnnouncement = (studyID, announcementID) => {
  return dispatch => {
    const url = `/control/studies/${studyID}/announcements/${announcementID}`
    return dispatch(
      request({
        url,
        success: json => Promise.resolve(json),
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const _getSMS = (studyID, smsID) => {
  return dispatch => {
    const url = `/control/studies/${studyID}/sms/${smsID}`
    return dispatch(
      request({
        url,
        success: json => Promise.resolve(json),
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

function fetchAnnouncementJson({ studyID, instrumentId, version, useLoader, instrument, redirectOnFail = true }) {
  const { deployed } = instrument || {}
  return (dispatch, getState) => {
    const versionText = version ? `?version=${version}` : ''
    const translationFileErrorMessage =
      'There was a problem with translation file. Please try again later or re-create the announcement'

    const redirectToCommunication = () => {
      const studyVersionID = getStudyVersionID(getState()?.location)
      browserHistory.push(`/studies/${studyID}/${studyVersionID}/communication`)
    }

    const handleAnnouncementJsonFailure = (res, content) => {
      dispatch(loadingActions.stopLoader(true))
      if (redirectOnFail) {
        dispatch(
          notyActions.showError({
            text: generateNotyMessage(translationFileErrorMessage, false),
          }),
        )
      }
      logSentryEvent({
        message: 'Fetch announcement.json failed',
        level: 'error',
        tags: {
          module: 'announcements',
          action: 'fetchAnnouncementJson',
          'http.status': res?.status,
        },
        contexts: {
          announcement: {
            studyID,
            instrumentId,
            serverMessage: content?.message,
          },
        },
      })
      if (redirectOnFail) redirectToCommunication()
      // Resolve (do not throw/reject): validateSession treats request rejections as logout.
      return Promise.resolve(null)
    }

    function success(json) {
      return Promise.resolve({ ...json, deployed })
    }

    if (!useLoader) dispatch(loadingActions.startLoader(true))

    return dispatch(
      request({
        method: 'GET',
        url: `/control/admin/studies/${studyID}/instruments/${instrumentId}/announcement.json${versionText}`,
        success,
        fail: handleAnnouncementJsonFailure,
        // Avoid logout if the API returns 401 after a malformed translation payload / 500.
        logoutOnUnauthorized: false,
        catchMessage: translationFileErrorMessage,
      }),
    ).then(payload => {
      // Network failures may resolve without a payload; keep the same recovery UX.
      if (!payload) {
        dispatch(loadingActions.stopLoader(true))
        if (redirectOnFail) redirectToCommunication()
        return null
      }
      return payload
    })
  }
}

const _saveCommunicationToDB = (studyID, siteID, communication, deploy, isAnnouncement = true) => {
  return (dispatch, getState) => {
    const { study } = getState()
    const { currentStudy } = study
    const isNewInstrumentArchitecture = currentStudy?.config?.instrument_architecture_version >= 2

    const isPUT = !!communication.id
    if (!isPUT) {
      communication.study_id = studyID
    }
    dispatch(sendingActions.startSender(deploy))
    if (isNewInstrumentArchitecture && isAnnouncement) {
      return dispatch(
        _saveAnnouncementToDbAsInstrument({ studyID, announcement: communication, deploy, isUpdate: isPUT }),
      )
    }
    const subUrl = isAnnouncement ? 'announcements' : 'sms'
    return dispatch(
      request({
        method: isPUT ? 'PUT' : 'POST',
        url: `/control/${isPUT ? `studies/${studyID}/${subUrl}/${communication.id}` : subUrl}`,
        body: JSON.stringify(communication),
        successMessage: deploy ? undefined : `${isAnnouncement ? 'Announcement' : 'SMS message'} successfully saved`,
        success: res => {
          return Promise.resolve(Object.assign(res, { studyID, siteID, deploy }))
        },
        hasSender: true,
      }),
    )
  }
}

const _deployCommunication = (studyID, communicationID, isAnnouncement = true) => {
  return (dispatch, getState) => {
    const studyVersionID = getStudyVersionID(getState()?.location)
    return dispatch(
      request({
        method: 'PATCH',
        url: `/control/studies/${studyID}/${isAnnouncement ? 'announcements' : 'sms'}/${communicationID}/deploy`,
        successMessage: `${isAnnouncement ? 'Announcement' : 'SMS message'} successfully saved and deployed`,
        success: res => {
          dispatch(sendingActions.stopSender(true))
          setTimeout(() => {
            browserHistory.push(`/studies/${studyID}/${studyVersionID}/communication`)
            dispatch(sendingActions.resetSender(true))
          }, 500)
        },
        hasSender: true,
      }),
    )
  }
}

const _formatAnnouncementToInstrument = (announcement, isUpdate = false, studyVersionID) => {
  const _announcement = { ...announcement }
  _announcement.type = INSTRUMENT_TYPE_MAP.announcement

  /**
   * For Announcement instruments we want to package announcement content into metadata object
   * to allow the participant client apps to have the relevant content information without making
   * an additional request.
   */
  const { title, content, translations = {} } = announcement
  const announcement_content = { title, content }
  const metadata = { schedule: announcement.schedule }

  const translationKeys = Object.keys(translations)
  if (translationKeys.length) {
    announcement_content.translations = translations
    metadata.other_languages = translationKeys
  }
  metadata.announcement_content = announcement_content
  metadata.display_name = title
  _announcement.metadata = metadata
  if (isUpdate) {
    return _announcement
  }
  return { data: _announcement, format: 'json', study_version_id: studyVersionID }
}

export const _saveAnnouncementToDbAsInstrument = ({
  studyID,
  announcement,
  deploy,
  isUpdate,
  isSitePromotionUpdate = false,
}) => {
  const { id } = announcement

  return (dispatch, getState) => {
    const { study, location } = getState()
    const { studyVersionList, currentStudy } = study || {}
    const studyVersionID = getStudyVersionID(location)
    const body = isSitePromotionUpdate
      ? announcement
      : _formatAnnouncementToInstrument(announcement, isUpdate, studyVersionID)
    const hasStudyPromotion = !!currentStudy?.config?.study_promotion
    const isReadOnlyState = hasStudyPromotion && isVersionUatOrLive(studyVersionList, studyVersionID)

    return dispatch(
      request({
        method: isUpdate ? 'PUT' : 'POST',
        url: `/control/studies/${studyID}/instruments${isUpdate ? `/${id}` : ''}`,
        body: JSON.stringify(body),
        errorMessage: STRINGS.announcementSaveErr,
        successMessage: deploy ? undefined : `${!isReadOnlyState ? 'Announcement successfully saved' : ''}`,
        hasSender: true,
        success: res => {
          return Promise.resolve({
            ...res,
            studyID,
            deploy,
            isInstrument: true,
            ...(isUpdate && { id }), // Only include id if it's an update
          })
        },
      }),
    )
      .then(response => {
        if (isSitePromotionUpdate) {
          // Only fetch if the request was successful
          return dispatch(
            instrumentsActions.fetchInstruments({
              studyID,
              isAnnouncements: true,
            }),
          )
        }
        return response
      })
      .catch(error => {
        if (__DEV__) console.error('Save announcement failed:', error)
        throw error // Re-throw to maintain error propagation
      })
  }
}

const isAnnouncementInstrumentArchitecture = (state = {}) => {
  const instrumentArchitectureVersion =
    state?.study?.currentStudy?.config?.instrument_architecture_version || DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
  return instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
}

const downloadAnnouncementTranslationTemplate = ({ studyID, instrumentId, title, translations } = {}) => (
  dispatch,
  getState,
) => {
  const isV2Announcement = isAnnouncementInstrumentArchitecture(getState())

  if (isV2Announcement) {
    if (!instrumentId) {
      return dispatch(
        notyActions.showError({
          text: generateNotyMessage('Please save the announcement before downloading the translation template', false),
        }),
      )
    }

    return dispatch(
      request({
        url: `/control/studies/${studyID}/instruments/${instrumentId}/translations`,
        resType: 'blob',
        success: (blob, fileName) => {
          downloadBlob(blob, `study_${studyID}_${title || 'announcement'}_translation_${today(true)}.csv`, fileName)
        },
        fail: () => {
          logSentryEvent({
            message: 'Download announcement translation template failed',
            level: 'error',
            tags: {
              module: 'announcements',
              action: 'downloadAnnouncementTranslationTemplate',
            },
            contexts: {
              announcement: {
                studyID,
                instrumentId,
                title,
              },
            },
          })
          dispatch(
            notyActions.showError({
              text: generateNotyMessage('Please try again later', false),
            }),
          )
        },
      }),
    )
  }

  // V1 announcements still use the legacy convert_json endpoint
  return dispatch(
    request({
      body: JSON.stringify({ ...(translations || {}) }),
      method: 'POST',
      resType: 'blob',
      success: (blob, fileName) => {
        downloadBlob(blob, `announcement_translation.csv`, fileName)
      },
      url: '/control/announcements/convert_json',
      fail: () => {
        dispatch(
          notyActions.showError({
            text: generateNotyMessage('Please try again later', false),
          }),
        )
      },
    }),
  )
}

const applyImportedAnnouncementState = (dispatch, getState, payload, instrumentId, enforcedLanguage) => {
  const knownLocaleIds = getKnownLocaleIds(getState, enforcedLanguage)
  const importedTranslations = extractAnnouncementTranslations(payload, knownLocaleIds)
  const announcementPayload = {
    ...payload,
    id: payload?.id || instrumentId,
  }

  if (hasTranslationEntries(importedTranslations)) {
    announcementPayload.translations = importedTranslations
  } else {
    delete announcementPayload.translations
  }

  dispatch(setAnnouncement(announcementPayload))

  const cohortType = payload.schedule?.cohort?.type || payload.metadata?.schedule?.cohort?.type
  if (cohortType === 'study_groups') {
    const include =
      payload.schedule?.cohort?.filter?.include || payload.metadata?.schedule?.cohort?.filter?.include
    if (include) dispatch(initializeCheckedCohorts(include))
  }
}

const uploadAnnouncementTranslations = ({ studyID, instrumentId, file, successCb, enforcedLanguage } = {}) => (
  dispatch,
  getState,
) => {
  const isV2Announcement = isAnnouncementInstrumentArchitecture(getState())

  // Instrument-based announcements (V1 arch with instrument id, or V2 arch): PUT then refetch.
  if (instrumentId) {
    const success = () => {
      const refetchPromise = dispatch(onFetchInstrument(studyID, instrumentId)).then(instrument => {
        if (!instrument) return

        if (isV2Announcement) {
          return dispatch(
            fetchAnnouncementJson({
              studyID,
              instrumentId,
              instrument,
              useLoader: false,
              redirectOnFail: false,
            }),
          ).then(announcementJson => {
            const merged = mergeAnnouncementInstrumentAndJson(instrument, announcementJson, instrumentId)
            applyImportedAnnouncementState(dispatch, getState, merged, instrumentId, enforcedLanguage)
          })
        }

        return dispatch(
          fetchAnnouncementJson({
            studyID,
            instrumentId,
            instrument,
            useLoader: true,
            redirectOnFail: false,
          }),
        ).then(announcementJson => {
          const payload = announcementJson || buildAnnouncementFromInstrument(instrument, instrumentId)
          applyImportedAnnouncementState(dispatch, getState, payload, instrumentId, enforcedLanguage)
        })
      })

      return refetchPromise
        .then(() => {
          if (successCb) successCb()
        })
        .finally(() => {
          dispatch(loadingActions.stopLoader(true))
        })
    }

    return dispatch(
      request({
        method: 'PUT',
        url: `/control/studies/${studyID}/instruments/${instrumentId}/translations`,
        body: file,
        contentType: 'text/csv',
        hasLoader: true,
        successMessage: 'Translation successfully uploaded.',
        catchMessage: 'There was a problem uploading the file. Please try again later.',
        success,
        fail: () => {
          logSentryEvent({
            message: 'Upload announcement translations failed',
            level: 'error',
            tags: {
              module: 'announcements',
              action: 'uploadAnnouncementTranslations',
            },
            contexts: {
              announcement: {
                studyID,
                instrumentId,
              },
            },
          })
        },
      }),
    )
  }

  if (isV2Announcement) {
    return dispatch(
      notyActions.showError({
        text: generateNotyMessage('Please save the announcement before importing translations', false),
      }),
    )
  }

  // Legacy V1 announcements (no instrument id): convert_csv response hydrates Redux directly.
  return dispatch(
    request({
      body: file,
      method: 'POST',
      contentType: 'text/csv',
      success: payload => {
        if (successCb) successCb()
        dispatch(setAnnouncementTranslations(parseNewTranslations({ payloadTranslations: payload, enforcedLanguage })))
      },
      url: '/control/announcements/convert_csv',
      catchMessage: 'There was a problem uploading the file. Please try again later.',
    }),
  )
}

// UTIL FUNCTIONS

const _isEmptyAnnouncementContent = content => !content || !stripHtmlTags(content).trim()

const _announcementCohortValidation = (cohort, errors) => {
  if (cohort.type === GROUP_TYPE_MAP.cohorts && cohort.filter.include.length === 0) {
    errors['schedule.cohort.filter.include'] = 'Must assign at least one cohort.'
  }
  if (cohort.type === GROUP_TYPE_MAP.sites && cohort.filter.site_ids.length === 0) {
    errors['schedule.cohort.filter.sites'] = 'Must assign at least one site.'
  }
}

const _getAnnouncementErrors = announcement => {
  const errors = {}
  const { translations } = announcement
  // Check that all translations have subjects
  if (translations) {
    Object.keys(translations).forEach(languageId => {
      const translationErrors = []
      if (announcement.translations[languageId].title === '') {
        translationErrors.push('Subject line is empty')
      }
      if (_isEmptyAnnouncementContent(announcement.translations[languageId].content)) {
        translationErrors.push('Content is required')
      }
      if (translationErrors.length) {
        if (errors.translations) {
          errors.translations[languageId] = translationErrors.join('; ')
        } else {
          errors.translations = { [languageId]: translationErrors.join('; ') }
        }
      }
    })
  }
  if (announcement.title === '') errors.title = 'Subject line is empty'
  if (!translations && _isEmptyAnnouncementContent(announcement.content)) {
    errors.content = 'Content is required'
  }
  if (
    announcement.schedule.deploy.hasOwnProperty('first_login') &&
    announcement.schedule.deploy.first_login.interval === ''
  ) {
    errors['schedule.deploy.first_login.interval'] = 'Interval cannot be blank.'
  }

  if (announcement.schedule.deploy.absolute) {
    const today = toUTCMidnightSeconds(moment())
    if (announcement.schedule.deploy.absolute < today) {
      errors['schedule.deploy.absolute'] = 'Cannot travel to the past'
    } else if (announcement.schedule.deploy.absolute === 'INVALID') {
      errors['schedule.deploy.absolute'] = 'Please enter a valid date'
    } else {
      delete errors['schedule.deploy.absolute']
    }
  }

  if (announcement.schedule.recurring && announcement.schedule.recurring.interval === '') {
    errors['schedule.recurring.interval'] = 'Interval cannot be blank.'
  }

  if (Array.isArray(announcement.schedule.cohort)) {
    if (announcement.schedule.cohort.length === 0) {
      errors['schedule.cohort.filter.include'] = 'Must assign at least one'
    } else {
      announcement.schedule.cohort.forEach(cohort => {
        _announcementCohortValidation(cohort, errors)
      })
    }
  } else {
    _announcementCohortValidation(announcement.schedule.cohort, errors)
  }
  return errors
}

export const _getDefaultAnnouncement = () => {
  return {
    title: '',
    content: '',
    schedule: {
      deploy: {
        now: null,
        time_of_day: '09:00',
        new_participants: false,
      },
      cohort: {
        type: GROUP_TYPE_MAP.all,
      },
      expire: {
        never: null,
      },
    },
    deployed: null,
  }
}

const _getDefaultTranslations = localeId => {
  return {
    [localeId]: {
      title: '',
      content: '',
    },
  }
}
const _getDefaultTranslation = () => {
  return {
    title: '',
    content: '',
  }
}

//
// REDUCERS
//

const announcement = (state = _getDefaultAnnouncement(), action) => {
  const newState = { ...state }
  switch (action.type) {
    case SET_ALERT:
      return _formatAnnouncement(action.payload)
    case UPDATE_ALERT_FIELD:
      if (action.value === null) {
        delete newState[action.field]
      } else {
        newState[action.field] = action.value
      }
      return newState
    case UPDATE_ALERT_SCHEDULE:
      newState.schedule = { ...state.schedule }
      if (action.value === null) {
        delete newState.schedule[action.field]
      } else {
        newState.schedule[action.field] = action.value
      }
      return newState
    case RESET_ALERT:
      return _getDefaultAnnouncement()
    case TOGGLE_ANNOUNCEMENT_TRANSLATIONS: {
      if (newState.translations) {
        delete newState.translations
      } else if (action.localeId) {
        newState.translations = _getDefaultTranslations(action.localeId)
        newState.translations[action.localeId] = {
          title: newState.title || '',
          content: newState.content || '',
        }
      }
      return newState
    }
    case ADD_ANNOUNCEMENT_TRANSLATION: {
      if (!newState.translations) {
        newState.translations = {}
      }
      newState.translations[action.localeId] = _getDefaultTranslation()
      return newState
    }
    case UPDATE_ANNOUNCEMENT_TRANSLATION: {
      if (!newState.translations) {
        newState.translations = {}
      }
      if (!newState.translations[action.localeId]) {
        newState.translations[action.localeId] = _getDefaultTranslation()
      }
      newState.translations[action.localeId][action.key] = action.value
      return newState
    }
    case DELETE_ANNOUNCEMENT_TRANSLATION:
      if (newState.translations) {
        delete newState.translations[action.localeId]
      }
      return newState
    case SET_ANNOUNCEMENT_TRANSLATIONS:
      newState.translations = normalizeTranslationKeys(action.translations)
      return newState
    default:
      return state
  }
}

const errors = (state = {}, action) => {
  switch (action.type) {
    case SET_ALERT_ERRORS:
      return action.errors
    case UPDATE_ALERT_FIELD:
    case UPDATE_ALERT_SCHEDULE:
      if (state.hasOwnProperty(action.errorKey)) {
        const newState = { ...state }
        delete newState[action.errorKey]
        return newState
      }
      if (state.hasOwnProperty('schedule.cohort.filter.sites')) {
        const newState = { ...state }
        delete newState['schedule.cohort.filter.sites']
        return newState
      }
      return state
    case TOGGLE_COHORT: {
      const newState = { ...state }
      if (state.hasOwnProperty('schedule.cohort.filter.include')) {
        delete newState['schedule.cohort.filter.include']
      }
      return newState
    }
    case CLEAR_ANNOUNCEMENT_ERRORS:
      return {}
    default:
      return state
  }
}

export const announcementActions = {
  addAnnouncementTranslation,
  announcement,
  clearAnnouncementErrors,
  downloadAnnouncementTranslationTemplate,
  deleteAnnouncementTranslation,
  initializeAnnouncementPage,
  resetAnnouncement,
  saveAndDeploy,
  saveAnnouncement,
  toggleAnnouncementTranslations,
  updateAnnouncementField,
  updateAnnouncementSchedule,
  updateAnnouncementTranslation,
  uploadAnnouncementTranslations,
}

export default combineReducers({ announcement, errors })
